import { Box, MenuItem } from '@mui/material';
import { TOption } from '@verifime/utils';
import React from 'react';
import { Control, FieldErrorsImpl, FieldValues } from 'react-hook-form';
import FormCheckbox from '@componentsroot/Form/FormCheckbox';
import FormDate from '@componentsroot/Form/FormDate';
import FormFileInput from '@componentsroot/Form/FormFileInput';
import FormSelect from '@componentsroot/Form/FormSelect';
import FormTextInput from '@componentsroot/Form/FormTextInput';
import ResponsiveBox from '@componentsroot/ResponsiveBox';
import { RenderType, TRenderType } from '@componentsroot/Form/FormGenerator/utils';
import {
  TFormFieldsAndRules,
  TFromFieldInfo,
} from '@componentsroot/Form/FormGenerator/v1ForAddressOnly/utils';

const fieldComponentMapping: {
  [renderType in TRenderType]: (...props: any) => JSX.Element;
} = {
  [RenderType.Text]: ({
    fieldInfo,
    control,
    errors,
    onTextChange,
  }: {
    fieldInfo: TFromFieldInfo;
    control: Control<FieldValues, any>;
    errors: Partial<
      FieldErrorsImpl<{
        [x: string]: any;
      }>
    >;
    onTextChange?: (text: string) => void;
  }) => (
    <FormTextInput
      required={fieldInfo.showRequiredAsterisk} // To control whether show * or not in screen
      name={fieldInfo.fieldName}
      label={fieldInfo.label}
      type="text"
      margin="normal"
      fullWidth
      control={control}
      error={errors[fieldInfo.fieldName] as any}
      isValueUpperCase={fieldInfo.isValueUpperCase}
      onTextChange={onTextChange}
      disabled={fieldInfo.disabled}
    />
  ),
  [RenderType.Number]: () => {
    throw Error('Not implemented');
  },
  [RenderType.Select]: ({
    fieldInfo,
    control,
    formFieldsWithDefaultValues,
    initValue,
    onOptionChange,
    disabled,
  }: {
    fieldInfo: TFromFieldInfo;
    control: Control<FieldValues, any>;
    errors: Partial<
      FieldErrorsImpl<{
        [x: string]: any;
      }>
    >;
    formFieldsWithDefaultValues?: Record<string, any>;
    initValue: any;
    onOptionChange?: (value: any) => void;
    disabled?: () => boolean;
  }) => (
    <FormSelect
      required={fieldInfo.showRequiredAsterisk} // To control whether show * or not in screen
      name={fieldInfo.fieldName}
      label={fieldInfo.label}
      control={control}
      defaultValue={formFieldsWithDefaultValues?.[fieldInfo.fieldName] || initValue || ''}
      onOptionChange={(e) => {
        if (typeof onOptionChange === 'function') {
          onOptionChange(e.target.value);
        }
      }}
      disabled={disabled?.() || fieldInfo.disabled}
    >
      {typeof fieldInfo.renderItems === 'function'
        ? fieldInfo.renderItems()
        : fieldInfo.items?.map((option: TOption) => (
            <MenuItem key={option.code} value={option.code}>
              {option.label}
            </MenuItem>
          ))}
    </FormSelect>
  ),
  [RenderType.Date]: ({
    fieldInfo,
    control,
    formFieldsWithDefaultValues,
    initValue,
  }: {
    fieldInfo: TFromFieldInfo;
    control: Control<FieldValues, any>;
    errors: Partial<
      FieldErrorsImpl<{
        [x: string]: any;
      }>
    >;
    formFieldsWithDefaultValues?: Record<string, any>;
    initValue: any;
  }) => (
    <FormDate
      required={fieldInfo.showRequiredAsterisk} // To control whether show * or not in screen
      name={fieldInfo.fieldName}
      label={fieldInfo.label}
      control={control}
      maxDate={fieldInfo.maxDate}
      minDate={fieldInfo.minDate}
      defaultValue={formFieldsWithDefaultValues?.[fieldInfo.fieldName] || initValue || ''}
      disabled={fieldInfo.disabled}
    />
  ),
  // TODO Need to figure out how to combine GenerateFormFields_New and GenerateFormFields
  // Do not implement DateRange for old one.
  [RenderType.DateRange]: () => <></>,
  [RenderType.Checkbox]: ({
    fieldInfo,
    control,
    errors,
  }: {
    fieldInfo: TFromFieldInfo;
    control: Control<FieldValues, any>;
    errors: Partial<
      FieldErrorsImpl<{
        [x: string]: any;
      }>
    >;
  }) => (
    <FormCheckbox
      required={isShowRequiredAsterisk(fieldInfo)}
      name={fieldInfo.fieldName}
      label={fieldInfo.label}
      control={control}
      error={errors[fieldInfo.fieldName] as any}
      disabled={fieldInfo.disabled}
    />
  ),
  [RenderType.File]: ({
    fieldInfo,
    control,
    onFileChange,
  }: {
    fieldInfo: TFromFieldInfo;
    control: Control<FieldValues, any>;
    errors: Partial<
      FieldErrorsImpl<{
        [x: string]: any;
      }>
    >;
    onFileChange: (file: File) => void;
  }) => (
    <Box sx={{ width: '100%', my: '1rem' }}>
      <FormFileInput
        name={fieldInfo.fieldName}
        label={fieldInfo.label}
        control={control}
        helpText={fieldInfo?.helpText}
        onChange={onFileChange}
        required={fieldInfo.showRequiredAsterisk} // To control whether show * or not in screen
        disabled={fieldInfo.disabled}
      />
    </Box>
  ),
  // TODO Need to figure out how to combine GenerateFormFields_New and GenerateFormFields
  // Do not implement TextEditor for old one.
  [RenderType.TextEditor]: () => <></>,
  // Do not implement this for old one
  [RenderType.RadioGroup]: () => <></>,
  [RenderType.ComboBox]: () => <></>,
};

export default function GenerateFormFields({
  formFieldsAndRules,
  control,
  errors,
  formFieldsWithDefaultValues,
  fieldsOperations = {},
  ContainerComponent: ContainerComponentProp,
}: {
  formFieldsAndRules: TFormFieldsAndRules;
  control: Control<FieldValues, any>;
  errors: Partial<
    FieldErrorsImpl<{
      [x: string]: any;
    }>
  >;
  formFieldsWithDefaultValues?: Record<string, any>;
  /**
   * There are maybe several Select to be rendered in the `formFieldsAndRules`,
   * each of them may have different `initial value`, `onOptionChange` operations,
   * and those `onOptionChange` even `initial value` cannot be predefined in the `formFieldsAndRules`,
   * thus expose this to accept different operations of fields
   */
  fieldsOperations?: {
    [fieldName: string]: {
      initValue?: any;
      onOptionChange?: (value: any) => void;
      onFileChange?: (file: File) => void;
      onTextChange?: (text: string) => void;
      disabled?: () => boolean;
    };
  };
  ContainerComponent?: React.FunctionComponent<any>;
}) {
  // Must run on every render regardless of whether the caller supplies ContainerComponent, so it
  // is a hook called unconditionally in the body rather than a default parameter value (a default
  // expression only evaluates when the prop is omitted, which made this hook's call count depend
  // on the caller, not just on this component's own render). The default itself is applied here at
  // the point of use instead.
  const defaultContainerComponent = React.useCallback(ResponsiveBox, [ResponsiveBox]);
  const ContainerComponent = ContainerComponentProp ?? defaultContainerComponent;

  // TODO: Reorganise formFixeldsAndRules by the ticket https://greengatefintech.atlassian.net/browse/VME-501
  const toHaveRenderTypeFields = Object.entries(formFieldsAndRules || {}).filter(
    ([_, v]) => v.renderType != null,
  );

  let lineNumber = 0;
  type TFields = { [line: string]: [fieldName: string, fieldInfo: TFromFieldInfo][] };
  const toBeRenderedFields: TFields = toHaveRenderTypeFields.reduce(
    (tally: TFields, [fieldName, fieldInfo]) => {
      if (fieldInfo.isNewLine) {
        lineNumber += 1;
      }

      let key = `line${lineNumber}`;
      if (!tally[key]) {
        tally[key] = [];
      }
      tally[key].push([fieldName, fieldInfo]);
      return tally;
    },
    {},
  );

  if (Object.keys(toBeRenderedFields).length < 1) {
    return null;
  }

  return (
    <>
      {Object.entries(toBeRenderedFields).map(([line, fields]) => {
        return (
          <ContainerComponent key={line}>
            {fields.map(([fieldName, fieldInfo]) => {
              const { initValue, onOptionChange, onFileChange, onTextChange, disabled } =
                fieldsOperations?.[fieldName] || {};
              return (
                <React.Fragment key={fieldName}>
                  {fieldComponentMapping[fieldInfo.renderType]?.({
                    control,
                    errors,
                    fieldInfo,
                    formFieldsWithDefaultValues,
                    onOptionChange,
                    initValue,
                    onFileChange,
                    onTextChange,
                    disabled,
                  })}
                </React.Fragment>
              );
            })}
          </ContainerComponent>
        );
      })}
    </>
  );
}

export function isShowRequiredAsterisk(fieldInfo: TFromFieldInfo): boolean {
  return !fieldInfo.rules.describe().optional;
}
